import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { calculateTotalPay } from "@src/appV2/redesign/utils/calculateShiftPay";
import { getShiftWorkDurationInHours } from "@src/appV2/redesign/utils/getShiftWorkDuration";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";

import { EnsureChartingIsCompleteCard } from "./components/EnsureChartingIsCompleteCard";
import { ShiftOverviewCard } from "./components/ShiftOverviewCard";
import { TimesheetVerificationCard } from "./components/TimesheetVerificationCard";
import { WorkplaceCard } from "./components/WorkplaceCard";
import { getVerificationMessage } from "./utils/getVerificationMessage";

interface TimekeepingDetailsSubPageProps {
  shift: Shift;
}

export function TimekeepingDetailsSubPage(props: TimekeepingDetailsSubPageProps) {
  const { shift } = props;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shift.facility?.userId,
  });

  const doesFacilityRequireMandatoryBreak = shift.facility.requiresLunchBreak ?? false;

  const workDurationInHours = getShiftWorkDurationInHours(
    { start: shift.start, end: shift.end },
    doesFacilityRequireMandatoryBreak,
    mandatoryBreakDurationInMinutes
  );

  const hourlyPayFormatted = formatDollarsAsUsd(shift.pay ?? 0);
  const { formattedPayAmount: totalPayFormatted } = calculateTotalPay({
    hourlyPayRate: shift.pay ?? 0,
    hoursWorked: workDurationInHours,
  });

  const workplaceId = shift.facility.userId;

  return (
    <Stack spacing={4} sx={{ paddingTop: 9 }}>
      <ShiftOverviewCard
        shiftPeriod={shift.name}
        shiftStartIso={shift.start}
        shiftEndIso={shift.end}
        qualificationName={shift.agentReq}
        isInstantPay={shift.isInstantPay ?? false}
        hourlyPay={hourlyPayFormatted}
        totalPay={totalPayFormatted}
        workplaceTimezone={shift.facility.tmz ?? ""}
        breakDurationInMinutes={mandatoryBreakDurationInMinutes}
        requiresBreak={doesFacilityRequireMandatoryBreak}
      />

      <WorkplaceCard
        workplaceId={shift.facility.userId ?? ""}
        workplaceName={shift.facility.name}
        shiftId={shift._id}
      />

      {isDefined(workplaceId) && (
        <EnsureChartingIsCompleteCard shiftId={shift._id} workplaceId={workplaceId} />
      )}

      <TimesheetVerificationCard
        verificationMessage={getVerificationMessage(
          doesFacilityRequireMandatoryBreak,
          workDurationInHours,
          mandatoryBreakDurationInMinutes
        )}
      />
    </Stack>
  );
}
